import { useDeferredValue, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";

import { SearchIcon } from "@/components/icons/SearchIcon";
import { usePlayers } from "@/hooks/usePlayers";
import { useVirtualRows } from "@/hooks/useVirtualRows";
import { normalizeForSearch } from "@/lib/text";

const MAX_NAME_LENGTH = 15;
/** Matches the row height classes below; refined by measurement on mount. */
const ESTIMATED_ROW_HEIGHT = 64;

function Message({ children }: { readonly children: string }) {
  return <p className="py-4 font-bold text-white lg:text-xl">{children}</p>;
}

export function TicketsCard() {
  const { players, status } = usePlayers();
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const inputId = useId();

  const scrollRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLLIElement>(null);
  const [rowHeight, setRowHeight] = useState(ESTIMATED_ROW_HEIGHT);

  const filtered = useMemo(() => {
    const needle = normalizeForSearch(deferredQuery);
    if (needle === "") return players;

    return players.filter((player) => normalizeForSearch(player.name).includes(needle));
  }, [players, deferredQuery]);

  // Row height changes with the breakpoint, so it's measured rather than assumed.
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;

    const observer = new ResizeObserver(() => {
      const measured = row.getBoundingClientRect().height;
      if (measured > 0) setRowHeight(measured);
    });

    observer.observe(row);

    return () => {
      observer.disconnect();
    };
  }, [filtered.length]);

  // A new search should start from the top of its results.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [deferredQuery]);

  const { start, end } = useVirtualRows(scrollRef, { itemCount: filtered.length, itemHeight: rowHeight });
  const visible = filtered.slice(start, end);

  return (
    <div className="flex h-full flex-col rounded-2xl bg-brand-deep">
      <div className="flex h-14 shrink-0 items-center justify-center px-5">
        <h2 className="text-xl font-bold uppercase text-brand-sky select-none lg:text-3xl">Tickets</h2>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 rounded-2xl bg-brand-sky p-5 lg:p-8">
        <div className="relative shrink-0">
          <label className="sr-only" htmlFor={inputId}>
            Procurar o teu nome
          </label>
          <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-brand-deep/50" />
          <input
            id={inputId}
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            placeholder="Procura o teu nome"
            className="w-full rounded-full bg-white py-3 pl-12 pr-4 font-bold text-brand-deep placeholder:font-medium placeholder:text-brand-deep/45 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-deep"
          />
        </div>

        <div ref={scrollRef} className="custom-scroll min-h-0 flex-1 overflow-y-auto pr-1">
          {status === "error" && <Message>Não foi possível carregar os tickets. Tenta novamente mais tarde.</Message>}

          {status === "ready" && players.length === 0 && <Message>Ainda não há tickets atribuídos.</Message>}

          {status === "ready" && players.length > 0 && filtered.length === 0 && <Message>Não encontrámos ninguém com esse nome.</Message>}

          {/* Only the visible slice is rendered; the padding stands in for the
              rows above and below so the scrollbar stays honest. */}
          <ul className="w-full" style={{ paddingTop: start * rowHeight, paddingBottom: Math.max(0, filtered.length - end) * rowHeight }}>
            {visible.map((player, offset) => {
              const position = start + offset;

              return (
                // Names repeat in the feed and there's no id, so the position is
                // part of the key.
                <li key={`${player.name}-${String(position)}`} ref={offset === 0 ? rowRef : undefined} className="flex h-16 items-center gap-5 pr-4">
                  <div className="h-12 w-12 shrink-0 lg:h-14 lg:w-14">
                    <img
                      className="h-full w-full rounded-full object-fill object-center"
                      src={player.avatar}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="font-extrabold text-brand-deep lg:text-xl xl:text-2xl">{player.name.substring(0, MAX_NAME_LENGTH)}</div>
                  <div className="flex-1 text-right font-bold text-white lg:text-xl xl:text-2xl">
                    {player.total} <span className="md:hidden lg:inline">tickets</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
